import {
  webDocumentResearchToolAllowedDomainSchema,
  webDocumentResearchToolMaxAllowedDomainCount,
} from "@repo/app-types/entities/tools/webDocumentResearch";
import type { WebDocumentResearchToolFormSettings } from "../components/webDocumentResearchToolForm.js";

type AllowedDomainsChange = Pick<
  WebDocumentResearchToolFormSettings,
  "allowedDomains" | "allowedDomainsError"
>;

const tooManyAllowedDomainsError = `Add no more than ${webDocumentResearchToolMaxAllowedDomainCount.toString()} allowed domains.`;

export function buildInvalidAllowedDomainError(
  domain: string,
  message: string,
): string {
  return `"${domain}" is not a valid domain. ${message}`;
}

/**
 * The next allowed-domain list to show, given what the field just produced.
 *
 * New values are normalized the way saving normalizes them; an invalid one, or
 * one that would exceed the cap, is rejected by keeping the previous list.
 * Removals always apply.
 */
export function applyAllowedDomainsChange(
  current: readonly string[],
  next: readonly string[],
): AllowedDomainsChange {
  const kept = new Set(current);
  const allowedDomains: string[] = [];

  for (const value of next) {
    let domain = value;
    if (!kept.has(value)) {
      const result =
        webDocumentResearchToolAllowedDomainSchema.safeParse(value);
      if (!result.success) {
        return {
          allowedDomains: current,
          allowedDomainsError: buildInvalidAllowedDomainError(
            value,
            result.error.issues[0]?.message ?? "",
          ),
        };
      }
      domain = result.data;
    }

    if (!allowedDomains.includes(domain)) {
      allowedDomains.push(domain);
    }
  }

  // An existing over-cap list stays editable: only additions are rejected.
  if (
    allowedDomains.length > webDocumentResearchToolMaxAllowedDomainCount &&
    allowedDomains.length > current.length
  ) {
    return {
      allowedDomains: current,
      allowedDomainsError: tooManyAllowedDomainsError,
    };
  }

  return { allowedDomains, allowedDomainsError: undefined };
}
